import { Select } from '../select';
import { MenuComponent } from 'components/layout/menu';
import { InlineSvg } from 'components/svg';
import { FC, useEffect, useMemo, useState } from 'react';

export interface FilterDropdownOption {
  label: string;
  value: string | number;
}

export interface FilterDropdownField {
  key: string;
  label: string;
  type?: 'select' | 'text';
  options?: FilterDropdownOption[];
  placeholder?: string;
}

interface FilterDropdownButtonProps {
  fields: FilterDropdownField[];
  values: Record<string, string | number | undefined>;
  onApply: (values: Record<string, string | number | undefined>) => void;
  onReset: () => void;
}

export const FilterDropdownButton: FC<FilterDropdownButtonProps> = ({ fields, values, onApply, onReset }) => {
  const [localValues, setLocalValues] = useState<Record<string, string | number | undefined>>(values);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  useEffect(() => {
    MenuComponent.reinitialization();
  }, []);

  const filtersCount = useMemo(() => {
    return Object.values(values).filter((v) => v !== undefined && v !== '').length;
  }, [values]);

  const handleChange = (key: string, option: { value: string; label: string } | null) => {
    const raw = option?.value ?? '';
    setLocalValues((prev) => ({
      ...prev,
      [key]: raw === '' ? undefined : isNaN(Number(raw)) ? raw : Number(raw)
    }));
  };

  const handleTextChange = (key: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const handleApply = () => {
    onApply(localValues);
  };

  const handleReset = () => {
    const resetValues: Record<string, string | number | undefined> = {};
    fields.forEach((f) => {
      resetValues[f.key] = undefined;
    });
    setLocalValues(resetValues);
    onReset();
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-light-primary text-nowrap"
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-end"
      >
        <InlineSvg path="/media/icons/custom/filter-2.svg" className="svg-icon-2" />
        Filters {filtersCount > 0 && <span className="badge badge-light-secondary">{filtersCount}</span>}
      </button>
      <div className="menu menu-sub menu-sub-dropdown w-300px w-md-325px" data-kt-menu="true">
        <div className="px-7 py-5">
          <div className="fs-5 text-dark fw-bolder">Filter Options</div>
        </div>
        <div className="separator border-gray-200"></div>

        <div className="px-7 py-5">
          {fields.map((field) => (
            <div className="form-group mb-6" key={field.key}>
              <label className="form-label fs-6">{field.label}</label>
              {field.type === 'text' ? (
                <input
                  type="text"
                  className="form-control form-control-sm form-control-solid"
                  placeholder={field.placeholder}
                  value={localValues[field.key] != null ? String(localValues[field.key]) : ''}
                  onChange={(e) => handleTextChange(field.key, e.target.value)}
                />
              ) : (
                <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                  <Select
                    size="lg"
                    options={(field.options ?? []).map((o) => ({ value: String(o.value), label: o.label }))}
                    emptyLabel="All"
                    value={
                      localValues[field.key] != null
                        ? {
                            value: String(localValues[field.key]),
                            label:
                              field.options?.find((o) => String(o.value) === String(localValues[field.key]))?.label ??
                              ''
                          }
                        : { value: '', label: 'All' }
                    }
                    onChange={(option) => handleChange(field.key, option)}
                    isClearable={false}
                    isSearchable={false}
                    menuPortalTarget={null}
                    menuPosition="absolute"
                  />
                </div>
              )}
            </div>
          ))}

          <div className="d-flex justify-content-end mt-10">
            <button
              type="button"
              className="btn btn-light-primary fw-bold me-2"
              data-kt-menu-dismiss="true"
              onClick={handleReset}
            >
              Reset
            </button>
            <button type="button" className="btn btn-primary fw-bold" data-kt-menu-dismiss="true" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
